import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AppBar, Toolbar, Button, Box, IconButton, Menu, MenuItem, useMediaQuery, useTheme, Typography } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import Logo from './Logo';
import { useAuth } from '../context/AuthContext';
import NotificationBell from './NotificationBell';

const Navigation = () => {
  // Navigation component with Payment link
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { userEmail, logout } = useAuth();
  const [mobileMenuAnchor, setMobileMenuAnchor] = React.useState(null);
  const [userMenuAnchor, setUserMenuAnchor] = React.useState(null);

  const handleMobileMenuOpen = (event) => {
    setMobileMenuAnchor(event.currentTarget);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuAnchor(null);
  };

  const handleUserMenuOpen = (event) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  const handleLogout = () => {
    logout();
    handleUserMenuClose();
  };

  const navigationItems = [
    { label: 'Home', path: '/' },
    { label: 'Find Gyms', path: '/gyms' },
    { label: 'List Your Gym', path: '/list-gym' },
    { label: 'My Listings', path: '/my-listings' },
    { label: 'Bookings', path: '/bookings' },
    { label: 'Earnings', path: '/earnings' },
  ];

  const authItems = [
    { label: 'Login', path: '/login' },
    { label: 'Register', path: '/register' },
  ];

  return (
    <AppBar 
      position="static" 
      color="primary" 
      elevation={1}
      sx={{
        backgroundColor: '#2F4F4F',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}
    >
      <Toolbar sx={{ minHeight: 84, px: 2 }}>
        {/* Logo */}
        <Box sx={{ flexGrow: 0, mr: 2 }}>
          <Link to="/" style={{ textDecoration: 'none', color: 'inherit' }}>
            <Logo variant="compact" />
          </Link>
        </Box>

        {/* Desktop Navigation */}
        {!isMobile && (
          <Box sx={{ flexGrow: 1, display: 'flex', gap: 1 }}>
            {navigationItems.map((item) => (
              <Button
                key={item.path}
                component={Link}
                to={item.path}
                color="inherit"
                sx={{
                  minWidth: 'auto',
                  px: 3,
                  py: 1.5,
                  fontSize: '1.1rem',
                  fontWeight: location.pathname === item.path ? 600 : 400,
                  backgroundColor: location.pathname === item.path ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                  borderRadius: 1,
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  }
                }}
              >
                {item.label}
              </Button>
            ))}
            {/* Additional Payment Button */}
            <Button
              component={Link}
              to="/payment"
              color="inherit"
              variant="outlined"
              sx={{
                minWidth: 'auto',
                px: 3,
                py: 1.5,
                fontSize: '1.1rem',
                fontWeight: location.pathname === '/payment' ? 600 : 400,
                backgroundColor: location.pathname === '/payment' ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                borderColor: 'rgba(255, 255, 255, 0.3)',
                borderRadius: 1,
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                }
              }}
            >
              💳 Payment
            </Button>
          </Box>
        )}

        {/* Auth Buttons */}
        <Box sx={{ display: 'flex', gap: 1, ml: 'auto', alignItems: 'center' }}>
          {userEmail ? (
            <>
              {/* Logged in user info */}
              <Typography variant="body2" sx={{ color: 'white', mr: 1, display: { xs: 'none', sm: 'block' } }}>
                Logged in as {userEmail}
              </Typography>
              
              {/* Notification Bell */}
              <NotificationBell />
              
              {/* User menu button */}
              <IconButton
                color="inherit"
                onClick={handleUserMenuOpen}
                sx={{
                  minWidth: 48,
                  minHeight: 48,
                  '& .MuiSvgIcon-root': {
                    fontSize: '2rem'
                  }
                }}
              >
                <AccountCircleIcon />
              </IconButton>
            </>
          ) : (
            /* Not logged in - show login/register buttons */
            authItems.map((item) => (
              <Button
                key={item.path}
                component={Link}
                to={item.path}
                variant={item.label === 'Register' ? 'contained' : 'outlined'}
                color="inherit"
                sx={{
                  minWidth: 'auto',
                  px: 3,
                  py: 1.5,
                  fontSize: '1.1rem',
                  fontWeight: 500,
                  borderColor: 'white',
                  '&:hover': {
                    backgroundColor: item.label === 'Register' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.1)',
                  }
                }}
              >
                {item.label}
              </Button>
            ))
          )}
        </Box>

        {/* Mobile Menu Button */}
        {isMobile && (
          <IconButton
            color="inherit"
            onClick={handleMobileMenuOpen}
            sx={{ 
              ml: 1,
              minWidth: 48,
              minHeight: 48,
              '& .MuiSvgIcon-root': {
                fontSize: '2rem'
              }
            }}
          >
            <MenuIcon />
          </IconButton>
        )}
      </Toolbar>

      {/* Mobile Menu */}
      <Menu
        anchorEl={mobileMenuAnchor}
        open={Boolean(mobileMenuAnchor)}
        onClose={handleMobileMenuClose}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 200,
            backgroundColor: '#2F4F4F',
            color: 'white'
          }
        }}
      >
        {navigationItems.map((item) => (
          <MenuItem
            key={item.path}
            component={Link}
            to={item.path}
            onClick={handleMobileMenuClose}
            sx={{
              color: 'white',
              fontWeight: location.pathname === item.path ? 600 : 400,
              backgroundColor: location.pathname === item.path ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              }
            }}
          >
            {item.label}
          </MenuItem>
        ))}
      </Menu>

      {/* User Menu */}
      <Menu
        anchorEl={userMenuAnchor}
        open={Boolean(userMenuAnchor)}
        onClose={handleUserMenuClose}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 200,
            backgroundColor: '#2F4F4F',
            color: 'white'
          }
        }}
      >
        <MenuItem
          sx={{
            color: 'white',
            backgroundColor: 'transparent',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
            }
          }}
        >
          <Typography variant="body2" sx={{ color: 'white', fontWeight: 500 }}>
            {userEmail}
          </Typography>
        </MenuItem>
        <MenuItem
          component={Link}
          to="/payment"
          onClick={handleUserMenuClose}
          sx={{
            color: 'white',
            backgroundColor: 'transparent',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
            }
          }}
        >
          Payment Settings
        </MenuItem>
        <MenuItem
          onClick={handleLogout}
          sx={{
            color: 'white',
            backgroundColor: 'transparent',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
            }
          }}
        >
          Logout
        </MenuItem>
      </Menu>
    </AppBar>
  );
};

export default Navigation;